// This is a COPY of aux-services/auth/schema/auth-schema.ts. To modify, edit the original and then run aux-services/bin/build-auth.sh
export const FieldLength = 128;
export const IdLength = 64;
export const EmailLength = 100;
export const PasswordLength = 100;
// Length of the numeric code emailed after sign-up. Shared so the confirmation page can size and
// validate its input field exactly. Other codes (password reset, email change, one-time password)
// are unrelated and keep their own formats.
export const SignupCodeLength = 6;

export type AccountStatus = 'unconfirmed' | 'confirmed' | 'suspended' | 'wasntMe' | 'deleted';

// Data-residency region an account's data must live in (see lib/jurisdiction.ts).
export type Jurisdiction = 'EU' | 'US';

export interface RestMembership {
    teamName: string;
    teamId: string;
    memberId: string; // the user id in the subsystem
    memberName: string;
    active: boolean;
    capabilities: string;
    appId: string;
    urls?: Record<string, string>;
    instance?: string; // instance ID for sharded apps
}

export interface RestAccount {
    id: string;
    tag?: string; // used to identify test data etc
    nickName: string;
    firstName?: string;
    lastName?: string;
    googleId?: string;
    hasPassword: boolean; // whether a password is set (read-only; derived from passwordHash). A Google user who set a password has both.
    memberships: RestMembership[];
    confirmed: boolean;
    jurisdiction?: Jurisdiction; // data-residency region (read-only; set at signup). Shown on the account page.
}

export interface RestAccountUpdate extends Partial<Omit<RestAccount, 'id' | 'googleId' | 'hasPassword' | 'memberships' | 'confirmed' | 'jurisdiction'>> {
    password?: string;
    oldPassword?: string;
}

export interface RestAccountCreate extends RestAccountUpdate {
    email?: string;
    phone?: string;
    googleId?: string;

    password?: string;
    oldPassword?: string;
    userType?: 'user' | 'super';
    appId?: string;          // app the user is signing up for; enables invitation handling
    invitationCode?: string; // optional invitation code; empty/absent uses the default invitation
    secret?: string;         // shared secret that, together with a tag, unlocks the no-email-validation
                             // test-signup path (must match the server's TEST_SIGNUP_SECRET env). Used by
                             // the load tester; without it a tag alone no longer auto-confirms.
    requireEmailConfirmation?: boolean; // test-signup only (needs tag+secret): keep the account
                             // unconfirmed and send the confirmation email (diverted to the in-memory
                             // test mailbox) instead of auto-confirming, so the confirmation flow can be
                             // exercised. Ignored for non-test signups (they always require confirmation).
    turnstileToken?: string; // Cloudflare Turnstile token from the accept-terms page. Required for
                             // real signups (the signup request is only sent after the user accepted
                             // the ToS and passed the challenge there); test signups bypass it.
    jurisdiction?: Jurisdiction; // test-signup only (needs tag+secret / TEST_AUTH_MODE): override the
                             // geo-derived data residency so one test harness can exercise both US and
                             // EU behaviour. Ignored for real signups (residency stays header-derived).
}

export interface Login {
    email?: string;
    phone?: string;
    password: string;
    appId: string; // the app ID to filter memberships by
    memberId?: string; // selects the membership to use
    teamId?: string; // selects the team to use
}

export interface SwitchAccount {
    appId: string; // the app ID to filter memberships by
    memberId: string; // selects the membership to use
    teamId: string;   // selects the membership to use
    accountId?: string; // selects the account to use, for system users only
}

export interface LoginResult {
    id: string;
    authToken: string;
    memberships: RestMembership[]; // possible memberships if not selected yet
    status: AccountStatus;
    selectedMembershipId?: string; // if logged in as member, this is the selected membership memberid
    capabilities?: string; // if logged in as member, this is the capabilities string
    code?: string; // for test use only
    tag?: string; // used to identify test data etc
    encryptedEmail?: string; // email encrypted with the account symmetricKey; client stores it instead of plaintext
    // Per-app URL map ({ <appId>: { apiHost, cdnUrl } }) for the account's data-residency jurisdiction,
    // resolved from APP_URLS_{EU,US}. Sent on every login-type response so the client can reach the
    // correct (locale-appropriate) API server even before a membership exists — notably the first
    // createUser call right after signup, which would otherwise always hit the default initApiHost.
    appUrls?: Record<string, Record<string, string>>;
}

export interface PasswordResetRequest {
    email?: string;
    phone?: string;
}

export interface PasswordReset {
    // The account is identified either by id (from the reset link) or by email/phone (manual entry
    // on the reset page, where only the code is known from the email).
    id?: string;
    email?: string;
    phone?: string;
    resetToken: string;
    password: string;
}

export interface EmailConfirmation {
    id: string;
    confirmationToken: string;
    wasntMe?: boolean; // if true, the user was not the one who requested the confirmation
}

export interface RequestEmailChange {
    oldEmail: string;
    newEmail: string;
    password: string;
}

export interface ChangeEmail {
    newEmail: string;
    codeA: string;
    codeB: string;
}

export interface RestAdminDelete {
    tag: string;
    magic: string;
}

export interface RestTagSummary {
    tag: string;
    count: number;
}

// --- Retention / legal-hold admin views (downread/RETENTION-CONCEPT.md) ---------------------

export interface RestInfringementRef {
    holdId: string;
    reason: string;
    at: string; // ISO
}

// A deleted-user tombstone as shown in the admin Retention UI (pseudonymous by construction).
export interface RestDeletedUser {
    emailHash: string;
    createdAt: string;
    deletedAt: string;
    strikeCount: number;
    infringementRefs: RestInfringementRef[];
    banStatus: string; // 'none' | 'until:<ISO>' | 'permanent'
    banReason?: string;
    lastStrikeAt?: string;
    updatedAt: string;
}

// A legal-hold record for the admin Retention UI. The preserved post snapshot is NOT sent to the
// browser (it stays in the DB as the preserved record); `hasPostSnapshot` flags that it exists.
export interface RestHoldRecord {
    id: string;
    reportId: string;
    category: number; // 1=CSAM, 3=takedown evidence, 4=legal claims
    reason: string;
    action: string;
    feedId: string;
    postIndex?: number;
    creatorAccountId?: string;
    creatorEmailHash?: string;
    creatorSnapshot?: Record<string, unknown>;
    hasPostSnapshot: boolean;
    reporterRef?: Record<string, unknown>;
    strike: boolean;
    lawEnforcementHold: boolean;
    createdAt: string;
    expiresAt: string;
    releasedAt?: string;
}

export interface RestRetentionLogEntry {
    id: string;
    at: string;
    event: string;
    accountId?: string;
    emailHash?: string;
    details?: Record<string, unknown>;
}

export interface RestAdminAccountResult {
    id: string;
    nickName: string;
    email?: string;
    status: AccountStatus;
    userType: 'user' | 'super';
    jurisdiction: Jurisdiction;
    // Auth methods available on the account (not mutually exclusive: an account may have both, e.g.
    // a password user who later links Google). A Google-only user has hasGoogle && !hasPassword.
    hasGoogle: boolean;
    hasPassword: boolean;
    memberships: Array<{
        teamName: string;
        teamId: string;
        memberId: string;
        memberName: string;
        active: boolean;
        capabilities: string;
        appId: string;
        instance?: string;
    }>;
    // Ban status of this identity from its deletedUsers tombstone (RETENTION-CONCEPT §5), populated
    // on account lookup. undefined / 'none' => not banned; 'permanent' or 'until:<ISO>' => banned.
    banStatus?: string;
    // ISO 8601 timestamp of the account's last successful login, if it has ever logged in.
    lastLogin?: string;
}

// GET /auth/admin/accounts/summary — lightweight admin annotation for a batch of account ids
// (admin flag + last login). Lets the API-side "recent users" list flag privileged accounts and
// show activity without a full per-account lookup. Keyed by accountId; deleted accounts are omitted.
export interface RestAccountSummary {
    accountId: string;
    userType: 'user' | 'super';
    /** The account's Downread membership capabilities string, if a membership exists. */
    capabilities?: string;
    /** ISO 8601 timestamp of the last successful login, if any. */
    lastLogin?: string;
}

export interface RestAdminAccountUpdate {
    magic: string;
    accountId: string;
    userType?: 'user' | 'super';
    accountStatus?: AccountStatus;
}

// Admin-created account: an admin creates a confirmed account directly (no email confirmation flow),
// choosing the data-residency jurisdiction explicitly instead of deriving it from request headers.
export interface RestAdminAccountCreate {
    magic: string;
    email: string;
    password: string;
    jurisdiction: Jurisdiction;
    nickName?: string;
    tag?: string; // optional label to identify test/batch data (same meaning as the signup tag)
}

export interface RestInvitation {
    id: string;
    appId: string;
    name: string;
    code: string;
    capacity: number;       // remaining sign-ups
    planCode: string;
    creatorId?: string;
    createdAt: string;      // ISO date
    expiresAt?: string;     // ISO date; absent means it never expires
    isDefault: boolean;
}

export interface RestInvitationCreate {
    magic: string;
    appId: string;
    name: string;
    code: string;
    capacity: number;
    planCode: string;
    expiresAt?: string;
}

export interface RestInvitationUpdate {
    magic: string;
    name?: string;
    code?: string;
    capacity?: number;
    planCode?: string;
    expiresAt?: string | null; // null clears the expiration (never expires)
}

// Public, unauthenticated info shown on the sign-up page for a valid invite.
export interface RestInvitationPreview {
    name: string;
    planCode: string;
}

export interface OneTimePasswordRequest {
    email?: string;
    phone?: string;
}

export interface OneTimeLogin {
    email?: string;
    phone?: string;
    password: string;
    appId: string;
    memberId?: string;
    teamId?: string;
}
